import React, { useState, Fragment, useEffect } from "react";
import { NavLink as RouterNavLink, useLocation, useSearchParams, useNavigate } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { LoginButton } from "./buttons/login-button";
import logo from "../assets/images/brightloop-logo-std-light-mode.svg"
import { UserProfileImage, useUserContext } from "../features/users";
import { AppBar, Toolbar, Menu, MenuItem, Box, IconButton, Stack, Button, Typography, Divider } from "@mui/material";
import { useAuth0 } from "@auth0/auth0-react";
import { useBrandContext } from "../hooks/useBrandContext";
import { CircleHelp, ChevronDown, ChevronUp, ExternalLink, User, LogOut, Lock, Settings } from "lucide-react";
import { Link } from "react-router-dom";


export const AppNavBar = React.memo(({ hideUser = false }: { hideUser?: boolean }) => {

    const { userContext } = useUserContext();
    const { brandContext } = useBrandContext();
    const location = useLocation();

    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
    const [logoAnchorEl, setLogoAnchorEl] = useState<null | HTMLElement>(null);
    const [helpAnchorEl, setHelpAnchorEl] = useState<null | Element>(null);
    const open = Boolean(anchorEl);
    const logoMenuOpen = Boolean(logoAnchorEl);
    const helpMenuOpen = Boolean(helpAnchorEl);

    const handleClick = (event: React.MouseEvent<HTMLElement>) => {
        setAnchorEl(event.currentTarget);
    };

    const handleClose = () => {
        setAnchorEl(null);
    };

    const handleLogoClick = (event: React.MouseEvent<HTMLElement>) => {
        setLogoAnchorEl(event.currentTarget);
    };

    const handleLogoClose = () => {
        setLogoAnchorEl(null);
    };

    const handleHelpClick = (event: React.MouseEvent<SVGSVGElement>) => {
        setHelpAnchorEl(event.currentTarget);
    };

    const handleHelpClose = () => {
        setHelpAnchorEl(null);
    };

    const handleContactUs = () => {
        setHelpAnchorEl(null);
        // Open Intercom widget
        if (window.Intercom) {
            window.Intercom('show');
        }
    };

    const {
        user,
        isAuthenticated,
        logout,
    } = useAuth0();


    const logoutWithRedirect = () =>
        logout({
            logoutParams: {
                returnTo: window.location.origin,
            }
        });

    if (hideUser) {
        return (
            <AppBar position="static" color="default" elevation={0} sx={{ backgroundColor: '#f8f9fa', position: 'absolute', top: 0, height: '70px', zIndex: 1200, boxShadow: '0 0 5px #c9c9c9', width: '100%' }}>
                <Toolbar>
                </Toolbar>
            </AppBar>
        );

    }

    const isAdvisor = (window.location.hostname === brandContext.brand?.custom_domain || location.pathname === "advisor") && !location.pathname.includes("/invitation");

    return (
        <AppBar position="static" color="default" elevation={0} sx={{ paddingLeft: '240px', backgroundColor: '#ffffff', position: 'absolute', top: 0, height: '44px', zIndex: 10, borderBottom: '1px solid #e0e0e0', width: '100%' }}>
            <Toolbar>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', width: '100%' }}>
                    <Stack direction="row" spacing={2} alignItems="center" justifyContent="flex-end">
                        {!hideUser && !isAuthenticated && isAdvisor &&
                            <Fragment>
                                <Box key="1">
                                    <Button variant="text" onClick={() => {
                                        window.location.href = `https://${brandContext.brand?.tag}.${process.env.REACT_APP_APP_ORIGIN}/dashboard?theme=advisor`;
                                    }}>
                                        Login
                                    </Button>
                                </Box>
                            </Fragment>
                        }

                        {!hideUser && !isAuthenticated && window.location.hostname !== brandContext.brand?.custom_domain && !(location.pathname.indexOf("invitation/") > 0) && (
                            <Fragment>
                                <Box key="2">
                                    <LoginButton />
                                </Box>
                            </Fragment>
                        )}
                        {!hideUser && isAuthenticated && user && userContext.dbUser && (
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: '15px' }}>
                                <CircleHelp 
                                    size={20} 
                                    color="grey" 
                                    style={{ cursor: 'pointer' }}
                                    onClick={handleHelpClick}
                                />
                                <IconButton
                                    onClick={handleClick}
                                    size="small"
                                    aria-controls={open ? 'account-menu' : undefined}
                                    aria-haspopup="true"
                                    aria-expanded={open ? 'true' : undefined}
                                    sx={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: '4px',
                                        "&:hover": {
                                            backgroundColor: "transparent",
                                            cursor: "pointer"
                                        }
                                    }}
                                >
                                    <UserProfileImage user={userContext.dbUser} size="mini" sx={{ cursor: "pointer" }} />
                                    {open ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
                                </IconButton>
                                <Menu
                                    anchorEl={anchorEl}
                                    id="account-menu"
                                    open={open}
                                    onClose={handleClose}
                                    onClick={handleClose}
                                    transformOrigin={{ horizontal: 'right', vertical: 'top' }}
                                    anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}

                                    sx={{
                                        zIndex: 2001,
                                        "& .MuiMenuItem-root": {
                                            color: "#000000",
                                            padding: "10px 20px",
                                            margin: "0 0"
                                        },
                                        "& .MuiMenuItem-root a": {
                                            color: "#000000",
                                            textDecoration: "none"
                                        },
                                        "& .MuiMenuItem-root span": {
                                            color: "#000000"
                                        },
                                        "& .MuiDivider-root.MuiDivider-fullWidth": {
                                            margin: "0px"
                                        }
                                    }}
                                    PaperProps={{
                                        sx: {
                                            maxHeight: '400px',
                                            borderRadius: "10px",
                                            mt: 1,
                                        }
                                    }}
                                >
                                    {/* User name - no link needed */}
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '10px 20px 15px' }}>
                                        <UserProfileImage user={userContext.dbUser} size="medium" sx={{ cursor: "pointer" }} />
                                        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', justifyContent: 'flex-start' }}>
                                            <Typography variant="body1" sx={{ fontWeight: 'bold' }}>{userContext.dbUser?.profile?.name}</Typography>
                                            <Typography variant="body1" sx={{ fontWeight: 'normal' }}>{userContext.dbUser?.email_address}</Typography>
                                        </Box>
                                    </Box>
                                    <Divider sx={{ margin: "0px" }} />

                                    {/* Profile link */}
                                    <MenuItem 
                                        component={RouterNavLink} 
                                        to={`//app.${process.env.REACT_APP_APP_ORIGIN}/settings/profile`}
                                        sx={{ textDecoration: 'none' }}
                                    >
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                            <Settings size={16} />
                                            <span>Settings</span>
                                        </Box>
                                    </MenuItem>

                                    {/* Admin link */}
                                    {user['https://gutcheck.svpvc.com/roles']?.includes('admin') && (
                                        <Divider sx={{ margin: "0px" }} />
                                    )}
                                    {user['https://gutcheck.svpvc.com/roles']?.includes('admin') && (
                                        <MenuItem 
                                            component={RouterNavLink} 
                                            to={`//app.${process.env.REACT_APP_APP_ORIGIN}/admin`}
                                            sx={{ textDecoration: 'none' }}
                                        >
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                                <Lock size={16} />
                                                <span>Admin</span>
                                            </Box>
                                        </MenuItem>
                                    )}

                                    {/* Logout button */}
                                    <Divider sx={{ margin: "0px" }} />
                                    <MenuItem
                                        id="qsLogoutBtn"
                                        onClick={logoutWithRedirect}
                                        sx={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: '8px'
                                        }}
                                    >
                                        <LogOut size={16} />
                                        <span>Log Out</span>
                                    </MenuItem>
                                </Menu>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                                    <img
                                        src={logo.toString()}
                                        alt="Brightloop"
                                        onClick={handleLogoClick}
                                        style={{ height: '16px', cursor: 'pointer' }}
                                    />
                                    <Menu
                                        anchorEl={logoAnchorEl}
                                        open={logoMenuOpen}
                                        onClose={handleLogoClose}
                                        onClick={handleLogoClose}
                                        transformOrigin={{ horizontal: 'right', vertical: 'top' }}
                                        anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
                                        sx={{
                                            zIndex: 2001,
                                            "& .MuiMenuItem-root": {
                                                color: "#000000",
                                                padding: "10px 20px",
                                                margin: "0 0",
                                            },
                                            "& .MuiMenuItem-root a": {
                                                color: "#000000",
                                                textDecoration: "none",
                                                display: "flex",
                                                alignItems: "center",
                                                justifyContent: "space-between",
                                                gap: "20px",
                                                width: "100%"
                                            },
                                            "& .MuiMenuItem-root span": {
                                                color: "#000000"
                                            },
                                            "& .MuiDivider-root.MuiDivider-fullWidth": {
                                                margin: "0px"
                                            }
                                        }}
                                        PaperProps={{
                                            sx: {
                                                maxHeight: '400px',
                                                borderRadius: "10px",
                                                mt: 1,
                                            }
                                        }}
                                    >
                                        <MenuItem>
                                            <a
                                                href="https://brightloop.com"
                                                target="_blank"
                                                rel="noopener noreferrer"
                                            >
                                                <span>About BrightLoop</span>
                                                <ExternalLink size={14} />
                                            </a>
                                        </MenuItem>
                                    </Menu>
                                </Box>
                                <Menu
                                    anchorEl={helpAnchorEl}
                                    open={helpMenuOpen}
                                    onClose={handleHelpClose}
                                    onClick={handleHelpClose}
                                    transformOrigin={{ horizontal: 'right', vertical: 'top' }}
                                    anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
                                    sx={{
                                        zIndex: 2001,
                                        "& .MuiMenuItem-root": {
                                            color: "#000000",
                                            padding: "10px 20px",
                                            margin: "0 0",
                                        },
                                        "& .MuiMenuItem-root a": {
                                            color: "#000000",
                                            textDecoration: "none",
                                            display: "flex",
                                            alignItems: "center",
                                            justifyContent: "space-between",
                                            gap: "20px",
                                            width: "100%"
                                        },
                                        "& .MuiMenuItem-root span": {
                                            color: "#000000"
                                        },
                                        "& .MuiDivider-root.MuiDivider-fullWidth": {
                                            margin: "0px"
                                        }
                                    }}
                                    PaperProps={{
                                        sx: {
                                            maxHeight: '400px',
                                            borderRadius: "10px",
                                            mt: 1,
                                        }
                                    }}
                                >
                                    <MenuItem>
                                        <a
                                            href="https://info.brightloop.com"
                                            target="_blank"
                                            rel="noopener noreferrer"
                                        >
                                            <span>Help Center</span>
                                            <ExternalLink size={14} />
                                        </a>
                                    </MenuItem>
                                    <Divider sx={{ margin: "0px" }} />
                                    <MenuItem onClick={handleContactUs}>
                                        <span>Contact Us</span>
                                    </MenuItem>
                                </Menu>
                            </Box>
                        )}
                    </Stack>

                </Box>
            </Toolbar>
        </AppBar>
    );
});

